/**
 * LandingFAQ Component
 *
 * Two-column FAQ section with title on left and accordion on right.
 * Mobile layout stacks vertically.
 */
"use client";

import { useState } from "react";
import Link from "next/link";
import { AnimatedSection } from "@/components/atoms/AnimatedSection";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/molecules/ui/Accordion";
import { trackLandingFaqToggled } from "@/lib/analytics/client";

interface FAQItem {
  question: string;
  answer: React.ReactNode;
}

const FAQ_LINK_CLASS = "text-lev-orange underline hover:no-underline";

const faqItems: FAQItem[] = [
  {
    question: "What is Lev?",
    answer:
      "Lev is your AI co-founder that helps you turn rough ideas into structured ventures through guided brainstorming, validation, and execution.",
  },
  {
    question: "Can I use Lev for multiple ideas?",
    answer:
      "Yes! Depending on your plan, you can explore and develop multiple startup ideas simultaneously with Lev's guidance.",
  },
  {
    question: "How are my ideas and data protected?",
    answer: (
      <>
        Yes. You own everything you put into Lev — your ideas, Canvas content,
        and uploads belong to you. We don&rsquo;t train AI models on your data,
        and Lev employees don&rsquo;t browse your account. See our{" "}
        <Link href="/privacy" className={FAQ_LINK_CLASS}>
          Privacy Policy
        </Link>{" "}
        and{" "}
        <Link href="/terms" className={FAQ_LINK_CLASS}>
          Terms of Service
        </Link>{" "}
        for the full details.
      </>
    ),
  },
  {
    question: "How does the pricing work?",
    answer:
      "Lev offers flexible pricing tiers to meet founders wherever they are in their journey, from free exploration to premium features for serious builders.",
  },
  {
    question: "What kind of support do I get?",
    answer: (
      <>
        Lev provides 24/7 AI-powered support for ideation, validation, market
        research, and go-to-market strategy development. For anything else,
        email{" "}
        <a
          href="mailto:help@getlev.co"
          className={FAQ_LINK_CLASS}
        >
          help@getlev.co
        </a>
        .
      </>
    ),
  },
];

export function LandingFAQ() {
  const [openItem, setOpenItem] = useState<string | undefined>(undefined);

  const handleValueChange = (value: string) => {
    const isOpening = value !== openItem && value !== "";
    const itemIndex = value ? parseInt(value.replace("item-", ""), 10) : -1;
    const question = itemIndex >= 0 ? faqItems[itemIndex]?.question : "";

    if (question) {
      trackLandingFaqToggled({ question, isOpen: isOpening });
    }

    setOpenItem(value || undefined);
  };

  return (
    <section id="faq" className="bg-landing-bg-light py-20 px-4">
      <div className="w-full max-w-[1032px] mx-auto">
        <div className="flex flex-col md:flex-row gap-12 md:gap-20">
          {/* Left column - Title */}
          <AnimatedSection className="md:w-1/3">
            <h2 className="text-[28px] md:text-[36px] text-landing-dark tracking-tight leading-tight">
              Frequently Asked Questions
            </h2>
          </AnimatedSection>

          {/* Right column - Accordion */}
          <AnimatedSection delay={0.1} className="md:w-2/3">
            <Accordion
              type="single"
              collapsible
              className="w-full space-y-4"
              value={openItem}
              onValueChange={handleValueChange}
            >
              {faqItems.map((item, index) => (
                <AccordionItem
                  key={item.question}
                  value={`item-${index}`}
                  className="bg-white rounded-2xl border-none px-6"
                >
                  <AccordionTrigger className="text-left text-landing-dark hover:no-underline py-5">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-landing-secondary leading-relaxed pb-5">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}
